/**
 * MBA Lab — pure, DOM-free view logic for the ROI calculator: unit
 * conversion between form (percent/whole-unit) and engine (decimal) scales,
 * display formatting, clamp-note text, and chart-axis math. roi.js (the DOM
 * wiring) imports everything here rather than defining it inline - this
 * mirrors how finance.js and charter-model.js are already kept DOM-free from
 * their own UI-wiring siblings, and it's what makes any of this unit-testable
 * without a browser/jsdom.
 */
import { LIMITS, DEFAULTS, intRangeOr } from './finance.js';
import { fmt } from './core.js';

/** Strips thousands-separator commas (e.g. from a live-formatted currency
 * field's value, "80,000") before parsing - a bare parseFloat would stop at
 * the first comma and silently read "80,000" as 80. */
export function parseNumber(value) {
  const n = parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/** Formats a whole number with thousands separators for display in a
 * comma-formatted text input (e.g. 80000 -> "80,000"). Rounds to the nearest
 * integer - these fields (salary, tuition, scholarships) are always
 * whole-dollar amounts. Non-finite input -> '' (an empty field), matching
 * how a user clearing the field produces an empty string, not "0" or "NaN". */
export function formatNumberForInput(value) {
  if (!Number.isFinite(value)) return '';
  return Math.round(value).toLocaleString('en-US');
}

/**
 * Whether a currency field's `beforeinput` handler should reject a
 * just-typed character outright (never inserted at all), vs. letting it
 * through for reformatNumberInput() to clean up afterward. Restricted to
 * single-character insertions specifically: a multi-character insertion
 * (paste, autofill, drag-drop) can legitimately carry "$"/","/spaces from
 * wherever it was copied - rejecting the whole paste for that would be worse
 * UX than just stripping the non-digits, which reformatNumberInput already
 * does. A single typed non-digit character has no such legitimate case in a
 * whole-dollar-amount field, so it's blocked at the point of insertion
 * rather than inserted-then-stripped - the character should never appear in
 * the field at all, not even for an instant.
 * @param {string|null} data - InputEvent.data: the text about to be
 *   inserted, or null for non-character edits (backspace, delete, composition).
 */
export function shouldRejectTypedChar(data) {
  return typeof data === 'string' && data.length === 1 && !/[0-9]/.test(data);
}

/**
 * Reformats a comma-grouped number input's value after an edit (keystroke or
 * paste), preserving the cursor's position *relative to the surrounding
 * digits* rather than its raw character index - a naive re-render would
 * otherwise jump the cursor to the end of the field on every keystroke,
 * since inserting/removing a comma shifts every character after it.
 * @param {string} rawValue - the input's .value immediately after the
 *   browser applied the edit (may contain commas already, e.g. "80,00" mid-edit).
 * @param {number} cursorPos - the input's selectionStart at that same moment.
 * @returns {{formatted: string, cursorPos: number}}
 */
export function reformatNumberInput(rawValue, cursorPos) {
  const digitsBeforeCursor = rawValue.slice(0, cursorPos).replace(/\D/g, '').length;
  const digitsOnly = rawValue.replace(/\D/g, '');
  const formatted = digitsOnly === '' ? '' : formatNumberForInput(Number(digitsOnly));

  if (digitsBeforeCursor === 0) return { formatted, cursorPos: 0 };
  let digitsSeen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) digitsSeen++;
    if (digitsSeen === digitsBeforeCursor) return { formatted, cursorPos: i + 1 };
  }
  return { formatted, cursorPos: formatted.length };
}

export function roundToNearest(value, step) {
  return Math.round(value / step) * step;
}

export function formatApproxCurrency(value) {
  return `≈ ${fmt.currency(roundToNearest(value, 500))}`;
}

export function formatClampValue(value) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function formatYears(years) {
  if (years === 0) return 'right away';
  if (years === 1) return '1 year';
  return `${years} years`;
}

/**
 * The phrase to use after a bare "~" prefix - used by both the headline's
 * "Breakeven in ~X" sentence and the chart's breakeven-marker badge "~X".
 * Distinct from formatYears(0) itself: "right away" reads fine as a
 * standalone answer, but composed after "~" it produces the broken
 * "~right away" - years===0 here (breakeven at month 1 or 2, which rounds to
 * 0 at this tool's half-year granularity - distinct from status:'immediate',
 * which means month 0 exactly) gets its own phrasing instead.
 */
function tildeYearsPhrase(years) {
  if (years === 0) return 'a few months';
  return formatYears(years);
}

export function breakevenHeadlineText(breakeven) {
  if (breakeven.status === 'immediate') return 'Net positive from the start';
  if (breakeven.status === 'none') return 'No breakeven within 30 years';
  return `Breakeven in ~${tildeYearsPhrase(breakeven.years)}`;
}

/** Short "~X" fragment for the chart's breakeven-marker badge - shares
 * tildeYearsPhrase's years===0 handling with breakevenHeadlineText so the
 * two labels can never disagree or independently regress the same bug. */
export function breakevenBadgeLabel(years) {
  return `~${tildeYearsPhrase(years)}`;
}

export function chartAriaLabel(roi) {
  return `Chart of cumulative financial difference between the MBA and no-MBA paths over 30 years, ` +
    `in today's dollars. ${breakevenHeadlineText(roi.breakeven)}. Full breakdown in the cards below the chart.`;
}

/** Converts raw form values (whole percents, e.g. 8 meaning 8%) into the
 * decimal-scale inputs finance.js's computeRoi() expects (0.08). */
export function toEngineInputs(v) {
  return {
    currentSalary: v.currentSalary,
    noMbaRaiseRate: v.noMbaRaisePercent / 100,
    totalCost: v.totalCost,
    scholarships: v.scholarships,
    lengthMonths: v.lengthMonths,
    incomeDuringProgram: v.incomeDuringProgram,
    percentBorrowed: v.percentBorrowed / 100,
    loanRate: v.loanRatePercent / 100,
    loanTermYears: v.loanTermYears,
    postMbaSalary: v.postMbaSalary,
    postMbaRaiseRate: v.postMbaRaisePercent / 100,
    discountRate: v.discountRatePercent / 100,
  };
}

const FULL_TIME_FORMAT = 'MBA';

/**
 * Format-driven starting point for "Income during the program" - full-time
 * defaults to $0 (most full-time students don't work), every other format
 * defaults to the equivalent of continuing your current salary flat across
 * the program (those formats are built to be completed alongside a job).
 * Either way it's just a starting point: the field itself is always directly
 * editable, so someone working part-time through a "full-time" program (or
 * cutting back hours during an Executive/Part-Time/Online one) can say so.
 */
export function defaultIncomeDuringProgram(format, currentSalary, lengthMonths) {
  if (format === FULL_TIME_FORMAT) return 0;
  const salary = Number.isFinite(currentSalary) ? currentSalary : 0;
  // Clamped through the same intRangeOr the engine itself uses (not a
  // re-implementation) - a raw, not-yet-clamped field value (e.g. "200"
  // months, which normalizeInputs caps at 120) would otherwise inflate this
  // default well past what the engine actually simulates, which was enough
  // to produce a false "Net positive from the start" headline.
  const months = intRangeOr(lengthMonths, DEFAULTS.lengthMonths, LIMITS.lengthMonths);
  // Rounds UP, never down: the engine derives a per-month figure as
  // incomeDuringProgram / lengthMonths, and a plain Math.round() total can
  // land a hair (sub-cent) below the true flat salary/12 rate purely from
  // whole-dollar rounding - enough on its own to flip the breakeven headline
  // from "Net positive from the start" to "Breakeven in ~X years" for about
  // a third of realistic Part-Time MBA salary/length combinations.
  return Math.ceil(salary * (months / 12));
}

/** Explains what the "Income during the program" default assumes for the
 * current format, shown as a permanent field note (not a conditional
 * clamp-style one) so the assumption is visible before results even render -
 * see defaultIncomeDuringProgram(), which this text describes. Explicitly
 * calls out "a total, not a yearly figure" for the non-full-time case: the
 * field holds the sum across the whole program (e.g. ~$200,000 for a
 * 32-month program at a $75,000 salary), and a user who read past just
 * "your current salary" could reasonably type their literal annual figure
 * instead, understating income during the program by roughly lengthMonths/12x. */
export function incomeDuringProgramNoteText(format) {
  return format === FULL_TIME_FORMAT
    ? "Defaults to $0 - most full-time students don't work during the program. Add any part-time work, stipend, or employer pay here."
    : "Defaults to your current salary continued for the whole program - a total, not a yearly figure - assuming you keep working throughout. Lower this if you'll cut back hours or pay.";
}

const CLAMP_EPSILON = 0.001;

// Each entry compares a raw form field (in the same units the user typed -
// percent or whole units) against what finance.js's normalizeInputs()
// actually used (roi.inputs, in engine units - decimals for rates, plain
// integers for lengthMonths/loanTermYears), via `toDisplay`. This
// deliberately compares against the engine's real output rather than
// reimplementing rangeOr()/intRangeOr() a second time: lengthMonths and
// loanTermYears are *rounded* by intRangeOr before being range-clamped, and
// a second implementation that only clamped (no rounding) would silently
// disagree with the engine for any in-range fractional input - e.g. "1.4"
// years rounds to 1 inside the engine, but a plain min/max clamp sees 1.4 as
// already "in range" and reports nothing changed. That's exactly the "typo
// silently changes the results with no explanation" scenario this note
// exists to prevent.
export const CLAMPABLE_FIELDS = [
  { formKey: 'noMbaRaisePercent', engineKey: 'noMbaRaiseRate', noteEl: 'noMbaRaiseNote', min: LIMITS.raiseRate[0] * 100, max: LIMITS.raiseRate[1] * 100, unit: '%', toDisplay: (v) => v * 100 },
  { formKey: 'percentBorrowed', engineKey: 'percentBorrowed', noteEl: 'percentBorrowedNote', min: LIMITS.percentBorrowed[0] * 100, max: LIMITS.percentBorrowed[1] * 100, unit: '%', toDisplay: (v) => v * 100 },
  { formKey: 'loanRatePercent', engineKey: 'loanRate', noteEl: 'loanRateNote', min: LIMITS.loanRate[0] * 100, max: LIMITS.loanRate[1] * 100, unit: '%', toDisplay: (v) => v * 100 },
  { formKey: 'loanTermYears', engineKey: 'loanTermYears', noteEl: 'loanTermYearsNote', min: LIMITS.loanTermYears[0], max: LIMITS.loanTermYears[1], unit: ' years', toDisplay: (v) => v },
  { formKey: 'lengthMonths', engineKey: 'lengthMonths', noteEl: 'lengthMonthsNote', min: LIMITS.lengthMonths[0], max: LIMITS.lengthMonths[1], unit: ' months', toDisplay: (v) => v },
  { formKey: 'postMbaRaisePercent', engineKey: 'postMbaRaiseRate', noteEl: 'postRaiseNote', min: LIMITS.raiseRate[0] * 100, max: LIMITS.raiseRate[1] * 100, unit: '%', toDisplay: (v) => v * 100 },
  { formKey: 'discountRatePercent', engineKey: 'discountRate', noteEl: 'discountRateNote', min: LIMITS.discountRate[0] * 100, max: LIMITS.discountRate[1] * 100, unit: '%', toDisplay: (v) => v * 100 },
];

/**
 * Whether/what to tell the user about one CLAMPABLE_FIELDS entry - see
 * CLAMPABLE_FIELDS' doc comment for why this compares against the engine's
 * actual output instead of running a second min/max clamp.
 * @param {number} raw - the value as typed, in display units.
 * @param {object} normalizedInputs - computeRoi(...).inputs (engine units).
 * @param {object} field - one entry from CLAMPABLE_FIELDS.
 * @returns {{isClamped: boolean, text: string}}
 */
export function resolveClampNote(raw, normalizedInputs, field) {
  const clamped = field.toDisplay(normalizedInputs[field.engineKey]);
  const isClamped = Number.isFinite(raw) && Math.abs(raw - clamped) > CLAMP_EPSILON;
  if (!isClamped) return { isClamped: false, text: '' };
  const reason = clamped === field.max ? 'capped at the maximum' : clamped === field.min ? 'capped at the minimum' : 'rounded';
  return { isClamped: true, text: `Using ${formatClampValue(clamped)}${field.unit} for this calculation (${reason}).` };
}

export function niceStep(maxAbs) {
  const rough = maxAbs / 2;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough || 1)));
  const normalized = rough / magnitude;
  let step;
  if (normalized < 1.5) step = 1;
  else if (normalized < 3.5) step = 2.5;
  else if (normalized < 7.5) step = 5;
  else step = 10;
  return step * magnitude;
}

export function areaPath(months, xScale, yScale, zeroY, positive, color) {
  const top = months.map((m, i) => {
    const v = m.cumulativeDiscounted;
    const keep = positive ? v >= 0 : v <= 0;
    const y = keep ? yScale(v) : zeroY;
    return `${xScale(i)},${y}`;
  });
  const first = xScale(0);
  const last = xScale(months.length - 1);
  const d = `M${first},${zeroY} L${top.join(' L')} L${last},${zeroY} Z`;
  return `<path d="${d}" fill="${color}" fill-opacity="0.1" />`;
}
